import { store } from "@/store";
import { chatApi } from "@/services/(my-space)/api.chat";
import { socketManager } from "./socket-manager";
import { CHAT_CHIME_GAP_MS, shouldChime, shouldNotify } from "./chat-sound";
import { playNotificationSound } from "@/utils/notifications.util";
import { SOCKET_CHAT_EVENTS, SOCKET_NAME_SPACES } from "./socket.enum";
import type {
  ChatTypingActivity,
  IChatConversation,
  IChatConversationRef,
  IChatMessage,
  IChatPresence,
  IChatReadReceipt,
  IChatUserSummary,
} from "@/types/(my-space)/chat.interface";

/**
 * Synthetic event: a new message worth a popup (`IChatNotifyPayload`), by the
 * same rules as the sound. The bottom-right popups listen for it.
 */
export const CHAT_LOCAL_NOTIFY = "local:notify";

/** What a popup shows: the message and its chat as the caches last saw it. */
export interface IChatNotifyPayload {
  message: IChatMessage;
  conversation: CachedConversation | undefined;
  /** Set when it's someone reacting to my message rather than a new one. */
  reaction?: { emoji: string; user: IChatUserSummary | null; at: string };
}

/** REACTION_NEW: someone reacted to a message of mine. */
export interface IChatReactionEvent {
  conversationId: number;
  message: IChatMessage;
  emoji: string;
  user: IChatUserSummary | null;
  reactedAt: string;
}

/** A chat as the list / detail caches hold it (just what a popup needs). */
export type CachedConversation = Pick<
  IChatConversation,
  "id" | "type" | "name" | "imageUrl" | "isMuted" | "metadata" | "isSelf" | "canSend"
> & { peer: IChatConversation["peer"] };

/** Synthetic event after every (re)connect: open rooms fetch what they missed. */
export const CHAT_LOCAL_CONNECTED = "local:connected";

export type ChatHubListener = (event: string, payload: unknown) => void;

type InvalidateTag =
  | "Conversations"
  | "Unread"
  | "Settings"
  | "Media"
  | "Scheduled"
  | { type: "Conversation" | "Scheduled"; id: number };

/** The API caps a presence subscription at 200 users. */
const PRESENCE_BATCH = 200;

/**
 * App-wide side of chat, alive while the `/chat` socket is:
 * - fans socket events out to whichever chat views are mounted,
 * - keeps the RTK Query caches (list, badge, detail) fresh from those events,
 * - holds presence of watched colleagues, ref-counted so the server
 *   subscription lives exactly as long as some view shows that person.
 */
class ChatHub {
  private listeners = new Set<ChatHubListener>();
  private presence = new Map<number, IChatPresence>();
  private watchCount = new Map<number, number>();
  private presenceSubscribers = new Set<() => void>();
  private presenceVersion = 0;
  private pendingTags: InvalidateTag[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Chats on screen — the Chat page's room and open chat windows — counted,
   * as the same chat can be in both. Their new messages don't bump the badge
   * or pop up.
   */
  private openCounts = new Map<number, number>();

  get openConversationIds(): ReadonlySet<number> {
    return new Set(this.openCounts.keys());
  }

  isOpen(conversationId: number) {
    return this.openCounts.has(conversationId);
  }

  /** Marks a chat as on screen until the returned release is called (once). */
  markOpen(conversationId: number): () => void {
    this.openCounts.set(conversationId, (this.openCounts.get(conversationId) ?? 0) + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      const left = (this.openCounts.get(conversationId) ?? 1) - 1;
      if (left > 0) this.openCounts.set(conversationId, left);
      else this.openCounts.delete(conversationId);
    };
  }

  private lastChimeAt = 0;

  private get myUserId(): number | undefined {
    return store.getState().user?.user?.id;
  }

  subscribe(listener: ChatHubListener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private fanOut(event: string, payload: unknown) {
    this.listeners.forEach((l) => l(event, payload));
  }

  /** Socket event from the server. */
  dispatch(event: string, payload: unknown) {
    if (!payload || typeof payload !== "object") return;
    switch (event) {
      case SOCKET_CHAT_EVENTS.PRESENCE:
        this.applyPresence(payload as IChatPresence);
        break;
      case SOCKET_CHAT_EVENTS.MESSAGE_NEW: {
        const m = payload as IChatMessage;
        const mine = m.sender?.id === this.myUserId;
        this.invalidate("Conversations");
        if (!mine && !this.isOpen(m.conversationId)) {
          this.invalidate("Unread");
        }
        if (m.type === "system") {
          this.invalidate({ type: "Conversation", id: m.conversationId });
        }
        if (m.attachments?.length) this.invalidate("Media");
        this.chime(m);
        break;
      }
      case SOCKET_CHAT_EVENTS.MESSAGE_UPDATED:
        this.invalidate("Conversations");
        break;
      case SOCKET_CHAT_EVENTS.READ:
        if ((payload as IChatReadReceipt).userId === this.myUserId) {
          this.invalidate("Unread");
          this.invalidate("Conversations");
        }
        break;
      case SOCKET_CHAT_EVENTS.CONVERSATION_UPDATED:
        this.invalidate("Conversations");
        this.invalidate("Unread");
        this.invalidate({
          type: "Conversation",
          id: (payload as IChatConversationRef).conversationId,
        });
        break;
      case SOCKET_CHAT_EVENTS.CONVERSATION_REMOVED:
        this.invalidate("Conversations");
        this.invalidate("Unread");
        break;
      case SOCKET_CHAT_EVENTS.SETTINGS_UPDATED:
        this.invalidate("Settings");
        break;
      case SOCKET_CHAT_EVENTS.REACTION_NEW:
        this.reacted(payload as IChatReactionEvent);
        break;
      case SOCKET_CHAT_EVENTS.SCHEDULED_UPDATED:
        this.invalidate({
          type: "Scheduled",
          id: (payload as IChatConversationRef).conversationId,
        });
        break;
    }
    this.fanOut(event, payload);
  }

  onConnected() {
    this.fanOut(CHAT_LOCAL_CONNECTED, {});
    this.invalidate("Unread");
    this.invalidate("Conversations");
    // Scheduled ones may have gone out (or failed) while we were away.
    this.invalidate("Scheduled");
    // Server-side subscriptions died with the old socket.
    void this.subscribePresence([...this.watchCount.keys()]);
  }

  /** The notification sound for a new message, when the push would have rung. */
  /** The sound and the bottom-right popup for a new message, when the push would ring. */
  private chime(m: IChatMessage) {
    const now = Date.now();
    const conversation = this.cachedConversation(m.conversationId);
    const ctx = {
      myUserId: this.myUserId,
      openConversationIds: this.openConversationIds,
      isWatching: document.visibilityState === "visible" && document.hasFocus(),
      isMuted: conversation?.isMuted,
    };
    if (!shouldNotify(m, ctx)) return;
    const payload: IChatNotifyPayload = { message: m, conversation };
    this.fanOut(CHAT_LOCAL_NOTIFY, payload);
    if (!shouldChime(m, { ...ctx, now, lastChimeAt: this.lastChimeAt })) return;
    this.lastChimeAt = now;
    playNotificationSound();
  }

  /**
   * Someone reacted to my message: the popup and sound, like a new message —
   * not for the chat I'm looking at, not if I muted it (the server checks
   * mute too; the cache may know of a newer one).
   */
  private reacted(e: IChatReactionEvent) {
    if (!e.message) return;
    const conversation = this.cachedConversation(e.conversationId);
    const watching = document.visibilityState === "visible" && document.hasFocus();
    if (watching && this.openConversationIds.has(e.conversationId)) return;
    if (conversation?.isMuted) return;
    const payload: IChatNotifyPayload = {
      message: e.message,
      conversation,
      reaction: { emoji: e.emoji, user: e.user, at: e.reactedAt },
    };
    this.fanOut(CHAT_LOCAL_NOTIFY, payload);
    const now = Date.now();
    if (now - this.lastChimeAt < CHAT_CHIME_GAP_MS) return;
    this.lastChimeAt = now;
    playNotificationSound();
  }

  /** The chat as the cached list or detail last saw it (undefined: not cached). */
  cachedConversation(conversationId: number): CachedConversation | undefined {
    const queries = store.getState()[chatApi.reducerPath].queries;
    for (const q of Object.values(queries) as ({ endpointName?: string; data?: unknown } | undefined)[]) {
      if (!q?.data) continue;
      if (q.endpointName === "getChatConversation") {
        const d = q.data as CachedConversation;
        if (d.id === conversationId) return d;
      } else if (q.endpointName === "getChatConversations") {
        const pages = (q.data as { pages?: { data: CachedConversation[] }[] }).pages ?? [];
        for (const page of pages) {
          const c = page.data.find((x) => x.id === conversationId);
          if (c) return c;
        }
      }
    }
    return undefined;
  }

  /** A batch of cache invalidations per burst of events, not one per event. */
  private invalidate(tag: InvalidateTag) {
    this.pendingTags.push(tag);
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => {
      const tags = this.pendingTags;
      this.pendingTags = [];
      this.flushTimer = null;
      store.dispatch(chatApi.util.invalidateTags(tags));
    }, 300);
  }

  // ── Typing ────────────────────────────────────────────────────────────────

  sendTyping(conversationId: number, isTyping: boolean, activity: ChatTypingActivity = "typing") {
    socketManager.emit(
      SOCKET_NAME_SPACES.CHAT,
      isTyping ? SOCKET_CHAT_EVENTS.TYPING_START : SOCKET_CHAT_EVENTS.TYPING_STOP,
      { conversationId, activity },
    );
  }

  // ── Presence ──────────────────────────────────────────────────────────────

  watchPresence(userIds: Iterable<number>) {
    const fresh: number[] = [];
    for (const id of new Set(userIds)) {
      if (id === this.myUserId) continue;
      const n = this.watchCount.get(id) ?? 0;
      this.watchCount.set(id, n + 1);
      if (n === 0) fresh.push(id);
    }
    void this.subscribePresence(fresh);
  }

  unwatchPresence(userIds: Iterable<number>) {
    const dropped: number[] = [];
    for (const id of new Set(userIds)) {
      const n = this.watchCount.get(id);
      if (n === undefined) continue;
      if (n <= 1) {
        this.watchCount.delete(id);
        this.presence.delete(id);
        dropped.push(id);
      } else {
        this.watchCount.set(id, n - 1);
      }
    }
    if (!dropped.length) return;
    socketManager.emit(SOCKET_NAME_SPACES.CHAT, SOCKET_CHAT_EVENTS.PRESENCE_UNSUBSCRIBE, {
      userIds: dropped,
    });
    this.notifyPresence();
  }

  /** Subscribes and applies the server's snapshot (socket.io ack). */
  private async subscribePresence(userIds: number[]) {
    for (let i = 0; i < userIds.length; i += PRESENCE_BATCH) {
      const batch = userIds.slice(i, i + PRESENCE_BATCH);
      // Down → renewed by onConnected.
      const snapshot = await socketManager.emitWithAck<IChatPresence[]>(
        SOCKET_NAME_SPACES.CHAT,
        SOCKET_CHAT_EVENTS.PRESENCE_SUBSCRIBE,
        { userIds: batch },
      );
      if (Array.isArray(snapshot)) snapshot.forEach((p) => this.applyPresence(p));
    }
  }

  private applyPresence(p: IChatPresence) {
    if (typeof p?.userId !== "number" || !this.watchCount.has(p.userId)) return;
    this.presence.set(p.userId, p);
    this.notifyPresence();
  }

  private notifyPresence() {
    this.presenceVersion++;
    this.presenceSubscribers.forEach((cb) => cb());
  }

  getPresence = (userId: number | null | undefined) =>
    userId == null ? undefined : this.presence.get(userId);

  /** For useSyncExternalStore. */
  subscribePresenceChanges = (cb: () => void) => {
    this.presenceSubscribers.add(cb);
    return () => {
      this.presenceSubscribers.delete(cb);
    };
  };

  getPresenceVersion = () => this.presenceVersion;

  reset() {
    this.presence.clear();
    this.watchCount.clear();
    this.openCounts.clear();
    this.notifyPresence();
  }
}

export const chatHub = new ChatHub();
